import { useQuery } from '@apollo/client';
import {
  Check,
  Copy,
  FileCode,
  FileText,
  FileX,
  Loader2,
  MoreHorizontal,
  RefreshCw,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CancelCfdiDialog } from './CancelCfdiDialog';
import { downloadBase64Pdf, downloadXml } from './download-helpers';
import { INVOICES_QUERY } from './invoicing.gql';
import { INVOICE_STATUS_VALUES } from './types';

import type { Invoice, InvoiceStatusFilter } from './types';
import type { CrudColumn } from '@/shared/crud/CrudTable';

import { useBranchScope } from '@/modules/auth/useAuth';
import { CrudTable } from '@/shared/crud/CrudTable';
import { INVOICE_STATUS_FROM_GQL, INVOICE_STATUS_TO_GQL } from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';

interface InvoicesResponse {
  invoices: {
    items: Invoice[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

type StatusTone = 'success' | 'warning' | 'muted' | 'destructive' | 'outline';

// green = stamped, amber = in flight, grey = closed out, red = needs attention
const STATUS_TONE: Record<string, StatusTone> = {
  pending: 'warning',
  stamping: 'warning',
  stamped: 'success',
  cancel_requested: 'warning',
  cancelled: 'muted',
  cancel_failed: 'destructive',
  failed: 'destructive',
};

const IN_FLIGHT_STATUSES = new Set(['pending', 'stamping', 'cancel_requested']);

const PAGE_SIZE = 25;

function formatInvoiceDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

export function InvoicingPage(): JSX.Element {
  const { t } = useTranslation();
  const [status_filter, setStatusFilter] = useState<InvoiceStatusFilter>('');
  const [cancel_target, setCancelTarget] = useState<Invoice | null>(null);
  const [error_text, setErrorText] = useState<string | null>(null);
  const [search_text, setSearchText] = useState('');
  const [offset, setOffset] = useState(0);
  const [downloading_key, setDownloadingKey] = useState<string | null>(null);
  const [copied_id, setCopiedId] = useState<string | null>(null);
  const branch_id = useBranchScope();

  const { data, refetch, loading } = useQuery<InvoicesResponse>(INVOICES_QUERY, {
    variables: {
      offset,
      limit: PAGE_SIZE,
      status: status_filter === '' ? null : INVOICE_STATUS_TO_GQL[status_filter],
      branch_id,
    },
    fetchPolicy: 'network-only',
    pollInterval: 60_000,
  });

  const all_items = (data?.invoices.items ?? []).map((invoice) => ({
    ...invoice,
    status:
      INVOICE_STATUS_FROM_GQL[invoice.status as keyof typeof INVOICE_STATUS_FROM_GQL] ??
      invoice.status,
  }));

  // The API has no free-text filter: search narrows the page already loaded.
  const needle = search_text.trim().toLowerCase();
  const items =
    needle === ''
      ? all_items
      : all_items.filter((invoice) =>
          [
            invoice.rfc_receptor,
            invoice.legal_name_receptor,
            invoice.uuid,
            invoice.folio_number,
            invoice.series,
          ].some((field) => (field ?? '').toLowerCase().includes(needle)),
        );

  // While searching, the table shows only the matches of the current page, so its
  // pager reports that subset instead of the server totals.
  const server_page = data?.invoices.page ?? {
    total: 0,
    offset: 0,
    limit: PAGE_SIZE,
    has_more: false,
  };
  const page =
    needle === ''
      ? server_page
      : { total: items.length, offset: 0, limit: PAGE_SIZE, has_more: false };

  const runDownload = async (key: string, action: () => Promise<void>): Promise<void> => {
    if (downloading_key !== null) return;
    setErrorText(null);
    setDownloadingKey(key);
    try {
      await action();
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloadingKey(null);
    }
  };

  const onClickCopyUuid = async (invoice: Invoice): Promise<void> => {
    if (!invoice.uuid) return;
    try {
      await navigator.clipboard.writeText(invoice.uuid);
      setCopiedId(invoice.id);
      window.setTimeout(
        () => setCopiedId((current) => (current === invoice.id ? null : current)),
        1500,
      );
    } catch {
      // Clipboard can be blocked (insecure context / permissions); the UUID stays selectable.
    }
  };

  const columns: CrudColumn<Invoice>[] = [
    {
      key: 'folio',
      header_key: 'invoicing.col_folio',
      render: (invoice) => (
        <div className="min-w-0 space-y-0.5">
          <div className="font-medium">
            {invoice.series ? `${invoice.series}-` : ''}
            {invoice.folio_number ?? '—'}
          </div>
          {invoice.uuid ? (
            <div className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
              <span className="max-w-[10rem] truncate" title={invoice.uuid}>
                {invoice.uuid}
              </span>
              {copied_id === invoice.id ? (
                <Check className="h-3 w-3 shrink-0 text-emerald-600" aria-hidden />
              ) : null}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'date',
      header_key: 'invoicing.field_date',
      className: 'whitespace-nowrap text-muted-foreground',
      render: (invoice) => formatInvoiceDate(invoice.stamped_at ?? invoice.requested_at),
    },
    {
      key: 'receptor',
      header_key: 'invoicing.field_receptor',
      render: (invoice) => (
        <div className="min-w-0">
          <div className="max-w-[16rem] truncate" title={invoice.legal_name_receptor ?? undefined}>
            {invoice.legal_name_receptor ?? '—'}
          </div>
          <div className="font-mono text-xs text-muted-foreground">
            {invoice.rfc_receptor ?? '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'total',
      header_key: 'invoicing.field_total',
      align: 'right',
      className: 'whitespace-nowrap font-mono',
      render: (invoice) => `${formatCentsString(invoice.total_cents)} ${invoice.currency}`,
    },
    {
      key: 'status',
      header_key: 'invoicing.col_status',
      render: (invoice) => (
        <div className="space-y-1">
          <Badge variant={STATUS_TONE[invoice.status] ?? 'outline'} className="gap-1">
            {IN_FLIGHT_STATUSES.has(invoice.status) ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : null}
            {t(`invoicing.status_${invoice.status}`)}
          </Badge>
          {invoice.error_message ? (
            <p
              className="max-w-[14rem] truncate text-xs text-destructive"
              title={invoice.error_message}
            >
              {invoice.error_message}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header_key: 'invoicing.col_actions',
      align: 'right',
      width: 'w-16',
      render: (invoice) => {
        const can_cancel = invoice.status === 'stamped' || invoice.status === 'cancel_failed';
        const busy = downloading_key?.startsWith(`${invoice.id}:`) ?? false;
        if (!invoice.uuid && !can_cancel) return null;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label={t('invoicing.actions_menu')}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <MoreHorizontal className="h-4 w-4" aria-hidden />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {invoice.uuid ? (
                <>
                  <DropdownMenuItem
                    disabled={downloading_key !== null}
                    onSelect={() => {
                      void runDownload(`${invoice.id}:xml`, () => downloadXml(invoice));
                    }}
                  >
                    <FileCode className="h-4 w-4" aria-hidden />
                    {t('invoicing.download_xml')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={downloading_key !== null}
                    onSelect={() => {
                      void runDownload(`${invoice.id}:pdf`, () => downloadBase64Pdf(invoice));
                    }}
                  >
                    <FileText className="h-4 w-4" aria-hidden />
                    {t('invoicing.download_pdf')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      void onClickCopyUuid(invoice);
                    }}
                  >
                    <Copy className="h-4 w-4" aria-hidden />
                    {t('invoicing.copy_uuid')}
                  </DropdownMenuItem>
                </>
              ) : null}
              {invoice.uuid && can_cancel ? <DropdownMenuSeparator /> : null}
              {can_cancel ? (
                <DropdownMenuItem destructive onSelect={() => setCancelTarget(invoice)}>
                  <FileX className="h-4 w-4" aria-hidden />
                  {t('invoicing.cancel_action')}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">{t('invoicing.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('invoicing.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="search"
            className="h-9 w-64"
            value={search_text}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('invoicing.search_placeholder')}
            aria-label={t('invoicing.search_placeholder')}
          />
          <select
            className="flex h-9 rounded-md border border-input bg-background px-3 text-sm"
            aria-label={t('invoicing.filter_all_statuses')}
            value={status_filter}
            onChange={(e) => {
              setStatusFilter(e.target.value as InvoiceStatusFilter);
              setOffset(0);
            }}
          >
            <option value="">{t('invoicing.filter_all_statuses')}</option>
            {INVOICE_STATUS_VALUES.map((s) => (
              <option key={s} value={s}>
                {t(`invoicing.status_${s}`)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void refetch();
            }}
            disabled={loading}
          >
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} aria-hidden />
            <span>{t('common.refresh')}</span>
          </Button>
        </div>
      </header>

      {error_text ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error_text}
        </div>
      ) : null}

      <CrudTable
        rows={items}
        columns={columns}
        loading={loading && data === undefined}
        empty_label_key="invoicing.empty"
        page={page}
        onPageChange={setOffset}
        rowKey={(invoice) => invoice.id}
      />

      {cancel_target ? (
        <CancelCfdiDialog
          invoice={cancel_target}
          on_close={() => setCancelTarget(null)}
          on_done={async () => {
            setCancelTarget(null);
            await refetch();
          }}
          on_error={setErrorText}
        />
      ) : null}
    </div>
  );
}
